/**
 * ConnectedProductSpace -- wires URL params and Apollo queries to the library's
 * pure ProductSpaceRenderer component.
 *
 * This follows the "connected wrapper" pattern established by ConnectedTreemap.
 * During migration, existing chart code in src/visualization/charts/ continues
 * to work unchanged. New pages or apps can use this wrapper (or compose their
 * own) to adopt the library components.
 *
 * Data flow:
 *   URL params (usePageQueryParams)
 *     -> Apollo query (CPY_GROWTH_QUERY)
 *       -> metadata tree + lookups
 *         -> useProductSpaceData (processing)
 *           -> ProductSpaceRenderer (pure, from @atlas-int/viz)
 */

import React, { useEffect, useMemo, useState, memo } from "react";
import { useQuery } from "@apollo/client/react";
import { extent, index } from "d3";
import { useParentSize } from "@visx/responsive";
import { Zoom } from "@visx/zoom";
import { useProductspaceParams } from "../paramSchemas";
import { getCategoriesForChartLegend, getColorMap } from "@atlas-int/viz";
import { PRODUCT_CLASS_METADATA_KEY, ColorBy, LegendLabels, ServicesClass } from "@atlas-int/data";
import { useHiddenCategories } from "@atlas-int/viz/treemap";
import { LegendRow } from "@atlas-int/viz/shared";
import { CPY_GROWTH_QUERY } from "@atlas-int/data";
import { createProductHierarchyTree } from "@atlas-int/data";
import { getLocationLevelFromStringLocationId } from "@atlas-int/data";
import { LocationLevel } from "@atlas-int/data/graphql";
import { useDownload } from "@atlas-int/viz";
import {surface} from "@atlas-int/tokens";

// Product space chart internals
import {
  clusterColors,
  centroids,
  psColors,
  useProductSpaceData,
  useProductSpaceStaticData,
  HighlightOverlay,
  AnimatedZoomGroup,
  ProductSpacePlot,
  ProductOverlay,
  ClickIndicator,
  ChartLegend,
} from "@atlas-int/viz/productspace";
import { complexityColorScale, ComplexityColorScaleLegend } from "@atlas-int/viz/treemap";
import { ZoomControls } from "@atlas-int/viz/growth";
import {
  GraphLoading,
  CategoryLabels,
  GraphNotice,
  GraphNoticeType,
  TooltipStateIsolationProvider,
  TooltipStateBridge,
  useIsolatedTooltipPayload,
} from "@atlas-int/viz/shared";
import type { TooltipDatum } from "@atlas-int/viz/shared";
import ModalBase, { ModalType } from "../../components/modals/ModalBase";
import { VisualizationContentContainer } from "../VizGrid";
import VisualizationLayout from "../components/VisualizationLayout";
import { googleAnalyticsCustomEvent } from "../../sharedUtilities/analytics";

import type { CategoryDatum } from "@atlas-int/viz/shared";
import type { ProductMetadatum } from "@atlas-int/data/types";
import type { HierarchyNode } from "d3-hierarchy";

type CountryProductYearDatum = {
  productId: string;
  exportRca?: number;
  exportValue?: number;
  distance?: number;
  cog?: number;
  normalizedPci?: number;
  normalizedCog?: number;
  normalizedDistance?: number;
  year?: number;
  [key: string]: unknown;
};
type ProductYearDatum = {
  productId: string;
  pci?: number;
  exportValue?: number;
  exportValueGrowth5?: number;
  [key: string]: unknown;
};
type MetaDataNode = HierarchyNode<ProductMetadatum>;

const EMPTY_ARRAY: CountryProductYearDatum[] = [];

// ── Memoized tooltip content ────────────────────────────────────────
const ProductSpaceTooltipContent = memo(function ProductSpaceTooltipContent() {
  const tooltipState = useIsolatedTooltipPayload<TooltipDatum>();
  if (!tooltipState?.contentData) return null;
  const datum = tooltipState.contentData;
  if (datum.kind !== "product") return null;
  return <span css={{ fontSize: "12px" }}>{datum.nameEn}</span>;
});

// ── Memoized legend ─────────────────────────────────────────────────
const ProductSpaceLegend = memo(function ProductSpaceLegend({
  colorBy,
  categoriesForLegend,
  hiddenCategories,
  setHiddenCategories,
}: {
  colorBy: ColorBy;
  categoriesForLegend: CategoryDatum[];
  hiddenCategories: string[];
  setHiddenCategories: (v: string[]) => void;
}) {
  return (
    <>
      {colorBy === ColorBy.Green && (
        <CategoryLabels
          legendLabel={LegendLabels.Green}
          categories={categoriesForLegend}
          allowToggle={true}
          hiddenCategories={hiddenCategories}
          setHiddenCategories={setHiddenCategories}
          resetText="Show All Products"
          fullWidth={true}
        />
      )}
      {colorBy === ColorBy.Sector && (
        <CategoryLabels
          legendLabel={LegendLabels.Sector}
          categories={categoriesForLegend}
          allowToggle={true}
          hiddenCategories={hiddenCategories}
          setHiddenCategories={setHiddenCategories}
          resetText="Show All Sectors"
          fullWidth={true}
        />
      )}
      {colorBy === ColorBy.Cluster && (
        <CategoryLabels
          legendLabel={LegendLabels.Cluster}
          categories={categoriesForLegend}
          allowToggle={true}
          hiddenCategories={hiddenCategories}
          setHiddenCategories={setHiddenCategories}
          resetText="Show All Clusters"
          fullWidth={true}
        />
      )}
      {colorBy === ColorBy.Complexity && (
        <ComplexityColorScaleLegend />
      )}
    </>
  );
});

const initialTransform = {
  scaleX: 0.9,
  scaleY: 0.9,
  translateX: 25,
  translateY: 25,
  skewX: 0,
  skewY: 0,
};

/**
 * ConnectedProductSpace reads all settings from URL query params, fetches data
 * via Apollo (CPY_GROWTH_QUERY), transforms it through the product space
 * pipeline, and renders via ProductSpaceRenderer.
 */
const ConnectedProductSpace: React.FC = () => {
  // === URL PARAMS ===
  const [
    {
      exporter,
      productClass,
      productLevel,
      view,
      year,
      colorBy,
      sizing,
      rcaThreshold,
    },
  ] = useProductspaceParams();

  // === LOCAL STATE ===
  const [selection, select] = useState(null);
  const [ringItem, setRingItem] = useState(null);
  const [highlightedItem, setHighlightedItem] = useState<string | undefined>(
    undefined,
  );
  const { svgRef, dataRef } = useDownload();

  // === LEGEND CATEGORIES ===
  const categoriesForLegend: CategoryDatum[] = useMemo(
    () =>
      getCategoriesForChartLegend({
        view,
        productClass,
        clusters: colorBy === ColorBy.Cluster,
        colorBySelection: colorBy,
        rcaThreshold,
      }).filter((d) => d.id.split("-")[2] !== "14"),
    [view, productClass, colorBy, rcaThreshold],
  );

  const { hiddenCategories, setHiddenCategories } = useHiddenCategories(
    view,
    colorBy,
  );

  // === DATA FETCHING ===
  const { loading, previousData, data } = useQuery<{
    countryProductYear: CountryProductYearDatum[];
    productYear: ProductYearDatum[];
    countryYear: Record<string, unknown>[];
    countryYearThresholds?: Record<string, unknown>[];
    [key: string]: unknown;
  }>(CPY_GROWTH_QUERY, {
    variables: {
      productClass,
      servicesClass: ServicesClass.unilateral,
      countryId: (() => {
        const raw = exporter?.split("-")[1];
        if (raw === undefined) return undefined;
        const n = parseInt(raw, 10);
        return Number.isNaN(n) ? undefined : n;
      })(),
      productLevel,
      yearMin: year,
      yearMax: year,
      hs12: productClass === "HS12",
      hs22: productClass === "HS22",
      hs92: productClass === "HS92",
      sitc: productClass === "SITC",
    },
  });

  const displayData = data ? data : previousData;
  const {
    countryProductYear = EMPTY_ARRAY,
    productYear = EMPTY_ARRAY,
  } = displayData || {};

  const metaDataTree = useMemo(() => {
    if (!displayData) {
      return createProductHierarchyTree([]);
    }
    const selectedMetadataKey =
      PRODUCT_CLASS_METADATA_KEY[productClass as string];
    const metaData = (
      (displayData as Record<string, unknown>)?.[selectedMetadataKey] ?? []
    ) as ProductMetadatum[];
    return createProductHierarchyTree(metaData);
  }, [displayData, productClass]);

  const metaDataLookup: Map<string, MetaDataNode> = useMemo(
    () =>
      index(metaDataTree.descendants(), (d) => d.id!) as Map<
        string,
        MetaDataNode
      >,
    [metaDataTree],
  );

  const productIdLookup: Map<string, MetaDataNode> = useMemo(() => {
    const descendants = metaDataTree.descendants() as MetaDataNode[];
    return index(
      descendants.filter((d) => d?.data?.productLevel === 4 && d?.data?.code),
      (d) => d.data.code as string,
    ) as Map<string, MetaDataNode>;
  }, [metaDataTree]);

  // === COLOR & LAYOUT ===
  const colorMap = useMemo(
    () => getColorMap({ view, productClass }),
    [view, productClass],
  );
  const clusterColorLookup = useMemo(
    () => index(clusterColors, (d) => d.cluster_name_short),
    [],
  );
  const { parentRef, width, height } = useParentSize({ debounceTime: 150 });

  const psStatic = useProductSpaceStaticData();
  const { xExtent, yExtent } = useMemo(() => {
    if (!psStatic) {
      return { xExtent: [undefined, undefined] as [number | undefined, number | undefined], yExtent: [undefined, undefined] as [number | undefined, number | undefined] };
    }
    const xExt = extent(psStatic.nodes, (d) => parseFloat(d.x));
    const yExt = extent(psStatic.nodes, (d) => parseFloat(d.y));
    return { xExtent: xExt, yExtent: yExt };
  }, [psStatic]);

  // === DATA PROCESSING ===
  const {
    totalExportLookup,
    countryExportLookup,
    xScale,
    yScale,
    rScale,
    linkLookup,
    processedData,
    findInVizOptions,
    totalValue,
    nodeLookup,
  } = useProductSpaceData({
    productYear,
    countryProductYear,
    metaDataLookup,
    productIdLookup,
    colorMap,
    clusterColorLookup: clusterColorLookup as Map<
      string,
      { cluster_col?: string }
    >,
    colorBySelection: colorBy,
    hiddenCategories,
    sizing,
    rcaThreshold,
    productClass,
    width,
    height,
    xExtent,
    yExtent,
  });

  // === DOWNLOAD DATA ===
  useEffect(() => {
    dataRef.current = processedData.map((d) => ({
      Name: metaDataLookup.get(d.productId)?.data?.nameEn,
      Code: d.product_code,
      Year: year,
      "Country Trade": countryExportLookup.get(d?.productId)?.exportValue,
      "World Trade": d.total_exports,
      "Revealed Comparative Advantage (RCA)": countryExportLookup.get(
        d?.productId,
      )?.exportRca,
      Sector: d.sector,
    }));
  }, [processedData, dataRef, metaDataLookup, year, countryExportLookup]);

  // === COUNTRY CHECK ===
  const isCountrySelected =
    exporter &&
    getLocationLevelFromStringLocationId(exporter) === LocationLevel.Country;

  if (!isCountrySelected) {
    return (
      <VisualizationLayout
        totalValue={undefined}
        findInVizOptions={[]}
        highlightedItem={undefined}
        onHighlight={() => {}}
      >
        <VisualizationContentContainer>
          <GraphNotice
            graphNoticeType={GraphNoticeType.CountrySelectionRequired}
          />
        </VisualizationContentContainer>
      </VisualizationLayout>
    );
  }

  // === RENDER ===
  return (
    <VisualizationLayout
      totalValue={totalValue}
      findInVizOptions={findInVizOptions}
      highlightedItem={highlightedItem}
      onHighlight={setHighlightedItem}
    >
      {ringItem && (
        <TooltipStateIsolationProvider>
          <ProductOverlay
            linkLookup={linkLookup}
            selection={ringItem}
            nodeLookup={nodeLookup}
            metaDataLookup={metaDataLookup}
            setRingItem={setRingItem}
            colorMap={colorMap}
            displayData={displayData}
            totalExportLookup={totalExportLookup}
            countryExportLookup={countryExportLookup}
            colorBySelection={colorBy}
            clusterColorLookup={clusterColorLookup}
            complexityColorScale={complexityColorScale}
            rcaThreshold={rcaThreshold as number}
            categories={categoriesForLegend}
            sizing={sizing}
            productClass={productClass}
            year={year}
            renderModal={({ onClose, children }) => (
              <ModalBase modalType={ModalType.ProductOverlay} toggleModal={onClose}>
                {children}
              </ModalBase>
            )}
          />
        </TooltipStateIsolationProvider>
      )}
      <TooltipStateIsolationProvider dataVersion={data} onHighlight={setHighlightedItem}>
        <TooltipStateBridge
          tooltipContent={<ProductSpaceTooltipContent />}
          clearHighlightedItem={() => setHighlightedItem(undefined)}
          overrideStyles={false}
          flexWidth
        >
          <VisualizationContentContainer>
            <div
              ref={parentRef}
              style={{ height: "100%", width: "100%", position: "absolute" }}
            >
              <ClickIndicator />
              <ChartLegend rScale={rScale} sizing={sizing} />
              <Zoom
                width={width}
                height={height}
                scaleXMin={1 / 2}
                scaleXMax={4}
                scaleYMin={1 / 2}
                scaleYMax={4}
                initialTransformMatrix={initialTransform}
              >
                {(zoom) => (
                  <>
                    {loading && <GraphLoading />}
                    <ZoomControls zoom={zoom} />
                    <svg
                      role="img"
                      aria-label="Product space network visualization"
                      height={height}
                      width={width}
                      style={{ backgroundColor: surface.white }}
                      ref={(el) => {
                        (
                          zoom.containerRef as React.MutableRefObject<SVGSVGElement | null>
                        ).current = el;
                        svgRef.current = el;
                      }}
                    >

                      <rect
                        width={width}
                        x={0}
                        height={height}
                        fill={surface.white}
                        onTouchStart={zoom.dragStart}
                        onTouchMove={zoom.dragMove}
                        onTouchEnd={zoom.dragEnd}
                        onMouseDown={zoom.dragStart}
                        onMouseMove={zoom.dragMove}
                        onMouseUp={zoom.dragEnd}
                        onMouseLeave={() => {
                          if (zoom.isDragging) zoom.dragEnd();
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Clear selection"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            setHighlightedItem(undefined);
                          }
                        }}
                        onClick={() => setHighlightedItem(undefined)}
                        style={{
                          touchAction: "none",
                          cursor: zoom.isDragging ? "grabbing" : "grab",
                          outline: "none",
                        }}
                        strokeWidth="1"
                        stroke={psColors.linkDefault}
                      />
                      <AnimatedZoomGroup zoom={zoom.transformMatrix}>
                        <ProductSpacePlot
                          processedData={processedData}
                          metaDataLookup={metaDataLookup}
                          highlightedItem={highlightedItem}
                          select={select}
                          setRingItem={setRingItem}
                          setHighlightedItem={setHighlightedItem}
                          onAnalyticsEvent={googleAnalyticsCustomEvent}
                        />
                        {selection && (
                          <HighlightOverlay
                            linkLookup={linkLookup}
                            selection={selection}
                            nodeLookup={nodeLookup}
                            metaDataLookup={metaDataLookup}
                            setHighlightedItem={setHighlightedItem}
                          />
                        )}
                        {highlightedItem && (
                          <HighlightOverlay
                            linkLookup={linkLookup}
                            selection={highlightedItem}
                            nodeLookup={nodeLookup}
                            metaDataLookup={metaDataLookup}
                            setHighlightedItem={setHighlightedItem}
                          />
                        )}
                        {zoom.transformMatrix.scaleX < 2 &&
                          clusterColors.map((c, i) => (
                            <text
                              key={centroids[i].FID}
                              id={centroids[i].FID}
                              textAnchor="middle"
                              x={xScale(centroids[i].X)}
                              y={yScale(centroids[i].Y)}
                              style={{ pointerEvents: "none" }}
                              stroke={psColors.bgSubtle}
                              strokeWidth="5"
                              strokeLinecap="butt"
                              strokeLinejoin="miter"
                              paintOrder="stroke"
                              fontWeight="800"
                            >
                              {c.cluster_name_short.toUpperCase()}
                            </text>
                          ))}
                      </AnimatedZoomGroup>
                    </svg>
                  </>
                )}
              </Zoom>
            </div>
          </VisualizationContentContainer>
        </TooltipStateBridge>
      </TooltipStateIsolationProvider>
      <LegendRow>
        <ProductSpaceLegend
          colorBy={colorBy}
          categoriesForLegend={categoriesForLegend}
          hiddenCategories={hiddenCategories}
          setHiddenCategories={setHiddenCategories}
        />
      </LegendRow>
    </VisualizationLayout>
  );
};

export default memo(ConnectedProductSpace);
